import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "./styles.module.css";
import { useEffect, useState } from "react";

/** Modo server-side: apenas estes tamanhos (sem "Todas" / total da consulta). */
const SERVER_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const Pagination = ({
  data,
  filteredData,
  setFilteredData,
  paginationKey = "default-pagination",
  // Modo server-side (apenas página Empresas): quando informado, usa total e callbacks
  serverSideTotal,
  serverSidePage = 1,
  serverSideRowsPerPage = 10,
  onServerPageChange,
  onServerRowsPerPageChange,
  // Modo server-side: tamanhos de página exatos (sem 500 nem o total da consulta); omitido mantém o padrão
  opcoesPorPagina,
  disabled = false,
}) => {
  const isServerSide = serverSideTotal != null && typeof onServerPageChange === "function";

  const [rowsPage, setRowsPage] = useState(isServerSide ? serverSideRowsPerPage : 10);
  const [page, setPage] = useState(isServerSide ? serverSidePage : 1);
  const [listRows, setListRows] = useState([]);

  const totalItems = isServerSide ? serverSideTotal : (data?.length ?? 0);
  const displayPage = isServerSide ? serverSidePage : page;
  const displayRowsPage = isServerSide ? serverSideRowsPerPage : rowsPage;
  const numbersPage =
    Math.ceil((data?.length ?? 0) / (displayRowsPage > 0 ? displayRowsPage : 10)) || 1;
  const totalPages = isServerSide ? (Math.ceil(totalItems / (displayRowsPage >= (totalItems || 1) ? (totalItems || 1) : displayRowsPage)) || 1) : numbersPage;
  const isLastPage = isServerSide ? displayPage >= totalPages : page >= numbersPage;
  const isFirstPage = isServerSide ? displayPage <= 1 : page === 1;

  // Intervalo exibido: "Mostrando X-Y de Z resultados"
  const startItem = totalItems === 0 ? 0 : (displayPage - 1) * displayRowsPage + 1;
  const endItem = totalItems === 0 ? 0 : Math.min(displayPage * displayRowsPage, totalItems);

  useEffect(() => {
    if (isServerSide) {
      setPage(serverSidePage);
      setRowsPage(serverSideRowsPerPage);
    }
  }, [isServerSide, serverSidePage, serverSideRowsPerPage]);

  // Se o estado ainda tiver limite antigo (ex.: 500 ou "total" como linhas por página), alinha ao conjunto permitido.
  useEffect(() => {
    if (!isServerSide || typeof onServerRowsPerPageChange !== "function") return;
    const permitidas = opcoesPorPagina ?? SERVER_PAGE_SIZE_OPTIONS;
    if (permitidas.includes(serverSideRowsPerPage)) return;
    onServerRowsPerPageChange(permitidas[0]);
  }, [isServerSide, serverSideRowsPerPage, onServerRowsPerPageChange, opcoesPorPagina]);

  useEffect(() => {
    if (isServerSide) return;
    const savedPage = Number(localStorage.getItem(`page-${paginationKey}`));
    if (!isNaN(savedPage) && savedPage > 0) setPage(savedPage);
    else setPage(1);
  }, [paginationKey, isServerSide]);

  useEffect(() => {
    if (isServerSide) return;
    localStorage.setItem(`page-${paginationKey}`, page);
  }, [page, paginationKey, isServerSide]);

  useEffect(() => {
    if (isServerSide) return;
    const totalPagesClient = Math.ceil((data?.length ?? 0) / rowsPage);
    const savedPage = Number(localStorage.getItem(`page-${paginationKey}`));
    if (!isNaN(savedPage) && savedPage > 0 && savedPage <= totalPagesClient) setPage(savedPage);
    else setPage(1);
  }, [data, rowsPage, paginationKey, isServerSide]);

  const handleDinamicRows = () => {
    if (isServerSide) {
      setListRows([...SERVER_PAGE_SIZE_OPTIONS]);
      return;
    }
    const rowsData = data?.length ?? 0;
    const rowsFixed = [10, 25, 50, 100];
    setListRows([...rowsFixed.filter((l) => l < rowsData), rowsData]);
  };

  useEffect(() => {
    if (isServerSide) return;
    if (rowsPage > 0 && data && setFilteredData) {
      const startIndex = (page - 1) * rowsPage;
      const pageSlice = data.slice(startIndex, startIndex + rowsPage);
      setFilteredData(pageSlice);
    }
    handleDinamicRows();
  }, [rowsPage, page, data, isServerSide]);

  const handleChangePage = (action) => {
    if (action === "first" && !isFirstPage) {
      if (isServerSide) onServerPageChange(1);
      else setPage(1);
    }
    if (action === "prev" && !isFirstPage) {
      if (isServerSide) onServerPageChange(displayPage - 1);
      else setPage((prev) => prev - 1);
    }
    if (action === "next" && !isLastPage) {
      if (isServerSide) onServerPageChange(displayPage + 1);
      else setPage((prev) => prev + 1);
    }
    if (action === "last" && !isLastPage) {
      if (isServerSide) onServerPageChange(totalPages);
      else setPage(totalPages);
    }
  };

  const handleChangeRowsPage = (e) => {
    const newRowsPage = Number(e.target.value);
    if (isServerSide) {
      onServerRowsPerPageChange(newRowsPage);
      return;
    }
    setRowsPage(newRowsPage);
    setPage(1);
  };

  if (isServerSide) {
    const startItem = totalItems === 0 ? 0 : (displayPage - 1) * Math.min(displayRowsPage, totalItems) + 1;
    const endItem = totalItems === 0 ? 0 : Math.min(displayPage * displayRowsPage, totalItems);
    const serverOptions = opcoesPorPagina ?? [10, 25, 50, 100, 500];
    const selectOptions =
      totalItems > 0 && !opcoesPorPagina
        ? [...serverOptions, ...(serverOptions.includes(totalItems) ? [] : [totalItems])]
        : serverOptions;

    return (
      <div className={styles.container}>
        <div className={styles.pagination}>
          <div className={styles.rowPerPage}>
            <span className={styles.label}>Itens por página:</span>
            <select onChange={handleChangeRowsPage} className={styles.select} value={displayRowsPage}>
              {selectOptions.map((valor) => (
                <option value={valor} key={valor}>
                  {valor}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.summary}>
            {totalItems === 0
              ? "Nenhum resultado"
              : `Mostrando ${startItem}-${endItem} de ${totalItems} resultados`}
          </div>
          <div className={styles.nav}>
            <button
              type="button"
              onClick={() => handleChangePage("first")}
              disabled={isFirstPage}
              className={isFirstPage ? styles.disabled : styles.button}
              title="Primeira página"
            >
              <FirstPageIcon fontSize="small" />
            </button>
            <button
              type="button"
              onClick={() => handleChangePage("prev")}
              disabled={isFirstPage}
              className={isFirstPage ? styles.disabled : styles.button}
              title="Página anterior"
            >
              <ChevronLeftIcon fontSize="small" />
            </button>
            <span className={styles.pageCurrent}>{displayPage}</span>
            <button
              type="button"
              onClick={() => handleChangePage("next")}
              disabled={isLastPage}
              className={isLastPage ? styles.disabled : styles.button}
              title="Próxima página"
            >
              <ChevronRightIcon fontSize="small" />
            </button>
            <button
              type="button"
              onClick={() => handleChangePage("last")}
              disabled={isLastPage}
              className={isLastPage ? styles.disabled : styles.button}
              title="Última página"
            >
              <LastPageIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Modo client-side: Usuários, Grupos, etc.
  return (
    <div className={styles.container}>
      <div className={styles.pagination}>
        <div className={styles.rowPerPage}>
          <span className={styles.label}>Itens por página:</span>
          <select onChange={handleChangeRowsPage} className={styles.select} value={rowsPage}>
            {listRows.map((linha) => (
              <option value={linha} key={linha}>
                {linha}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.summary}>
          {totalItems === 0
            ? "Nenhum resultado"
            : `Mostrando ${startItem}-${endItem} de ${totalItems} resultados`}
        </div>
        <div className={styles.nav}>
          <button
            type="button"
            onClick={() => handleChangePage("first")}
            disabled={isFirstPage}
            className={isFirstPage ? styles.disabled : styles.button}
            title="Primeira página"
          >
            <FirstPageIcon fontSize="small" />
          </button>
          <button
            type="button"
            onClick={() => handleChangePage("prev")}
            disabled={isFirstPage}
            className={isFirstPage ? styles.disabled : styles.button}
            title="Página anterior"
          >
            <ChevronLeftIcon fontSize="small" />
          </button>
          <span className={styles.pageCurrent}>{page}</span>
          <button
            type="button"
            onClick={() => handleChangePage("next")}
            disabled={isLastPage}
            className={isLastPage ? styles.disabled : styles.button}
            title="Próxima página"
          >
            <ChevronRightIcon fontSize="small" />
          </button>
          <button
            type="button"
            onClick={() => handleChangePage("last")}
            disabled={isLastPage}
            className={isLastPage ? styles.disabled : styles.button}
            title="Última página"
          >
            <LastPageIcon fontSize="small" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
